import React, { useState, useContext } from 'react';
import { 
  Button, 
  Card, 
  Typography, 
  message, 
  Space, 
  Table, 
  Input, 
  Select, 
  Popconfirm,
  Upload,
  Form
} from 'antd';
import { PlusOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import axios from 'axios';
import { AppContext } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import Papa from 'papaparse';

const { Title, Text } = Typography;
const { Option } = Select;

const BulkBorrowerSpreadsheet = () => {
  const [data, setData] = useState([]);
  const [editingKey, setEditingKey] = useState('');
  const [uploading, setUploading] = useState(false);
  const { company } = useContext(AppContext);
  const token = localStorage.getItem('token');
  const navigate = useNavigate();
  const [form] = Form.useForm();

  // Employment types for dropdown
  const employmentTypes = [
    'Fixed Term Contract', 'Permanent', 'Pensionable', 'Business Owner',
    'Freelancer', 'Informal', 'Formal Private', 'Government Employee'
  ];

  // Province types for dropdown
  const provinceTypes = [
    'Lusaka', 'Luapula', 'Copperbelt', 'Central', 'North Western',
    'Southern', 'Eastern', 'Muchinga', 'Northern', 'Western'
  ];

  // Relationship types for dropdown
  const relationshipTypes = [
    'Spouse', 'Parent', 'Child', 'Sibling', 'Friend', 'Colleague', 'Other'
  ];

  // Generate random reference number
  const generateRefNumber = () => {
    const prefix = 'REF';
    const timestamp = Date.now().toString().slice(-6);
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `${prefix}${timestamp}${random}`;
  };

  const isEditing = (record) => record.key === editingKey;

  const edit = (record) => {
    form.setFieldsValue({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      nrc: '',
      address: '',
      province: '',
      employment_type: '',
      business: '',
      department: '',
      reference_number: '',
      next_kin_name: '',
      next_kin_phone: '',
      next_kin_relationship: '',
      ...record,
    });
    setEditingKey(record.key);
  };

  const cancel = () => {
    setEditingKey('');
  };

  const save = async (key) => {
    try {
      const row = await form.validateFields();
      const newData = [...data];
      const index = newData.findIndex((item) => key === item.key);
      
      if (index > -1) {
        const item = newData[index];
        newData.splice(index, 1, {
          ...item,
          ...row,
          reference_number: row.reference_number || generateRefNumber(),
        });
        setData(newData);
        setEditingKey('');
      }
    } catch (errInfo) {
      console.log('Validate Failed:', errInfo);
    }
  };

  const handleDelete = (key) => {
    const newData = data.filter((item) => item.key !== key);
    setData(newData);
  };

  const handleAdd = () => {
    const newKey = Date.now().toString();
    const newData = {
      key: newKey,
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      nrc: '',
      address: '',
      province: '',
      employment_type: '',
      business: '',
      department: '',
      reference_number: generateRefNumber(),
      next_kin_name: '',
      next_kin_phone: '',
      next_kin_relationship: '',
    };
    setData([...data, newData]);
    edit(newData);
  };

  const loadSampleData = () => {
    const sampleData = [
      {
        key: '1',
        first_name: 'John',
        last_name: 'Doe',
        email: 'john.doe@example.com',
        phone: '260977123456',
        nrc: '123456/78/9',
        address: '123 Main Street, Lusaka',
        province: 'Lusaka',
        employment_type: 'Permanent',
        business: 'ABC Company',
        department: 'IT Department',
        reference_number: generateRefNumber(),
        next_kin_name: 'Jane Doe',
        next_kin_phone: '260977654321',
        next_kin_relationship: 'Spouse',
      },
      {
        key: '2',
        first_name: 'Mary',
        last_name: 'Smith',
        email: 'mary.smith@example.com',
        phone: '260977789012',
        nrc: '654321/87/1',
        address: '456 Oak Avenue, Ndola',
        province: 'Copperbelt',
        employment_type: 'Business Owner',
        business: 'Smith Enterprises',
        department: 'Operations',
        reference_number: generateRefNumber(),
        next_kin_name: 'Tom Smith',
        next_kin_phone: '260977210987',
        next_kin_relationship: 'Spouse',
      },
    ];
    setData(sampleData);
  };

  const clearAllData = () => {
    setData([]);
    setEditingKey('');
  };

  const EditableCell = ({
    editing,
    dataIndex,
    title,
    inputType,
    record,
    index,
    children,
    ...restProps
  }) => {
    let inputNode;
    
    if (inputType === 'select') {
      let options = [];
      if (dataIndex === 'province') options = provinceTypes;
      else if (dataIndex === 'employment_type') options = employmentTypes;
      else if (dataIndex === 'next_kin_relationship') options = relationshipTypes;
      
      inputNode = (
        <Select placeholder={`Select ${title}`}>
          {options.map(option => (
            <Option key={option} value={option}>{option}</Option>
          ))}
        </Select>
      );
    } else if (inputType === 'textarea') {
      inputNode = <Input.TextArea rows={2} />;
    } else {
      inputNode = <Input />;
    }

    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item
            name={dataIndex}
            style={{ margin: 0 }}
            rules={[
              {
                required: ['first_name', 'last_name', 'email', 'phone', 'nrc', 'address', 'province', 'employment_type', 'next_kin_name', 'next_kin_phone', 'next_kin_relationship'].includes(dataIndex),
                message: `Please input ${title}!`,
              },
            ]}
          >
            {inputNode}
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  };

  const columns = [
    {
      title: 'First Name',
      dataIndex: 'first_name',
      width: 120,
      editable: true,
    },
    {
      title: 'Last Name',
      dataIndex: 'last_name',
      width: 120,
      editable: true,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      width: 200,
      editable: true,
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      width: 120,
      editable: true,
    },
    {
      title: 'NRC/ID/Passport',
      dataIndex: 'nrc',
      width: 100,
      editable: true,
    },
    {
      title: 'Address',
      dataIndex: 'address',
      width: 200,
      editable: true,
      inputType: 'textarea',
    },
    {
      title: 'Province',
      dataIndex: 'province',
      width: 120,
      editable: true,
      inputType: 'select',
    },
    {
      title: 'Employment Type',
      dataIndex: 'employment_type',
      width: 150,
      editable: true,
      inputType: 'select',
    },
    {
      title: 'Business/Company',
      dataIndex: 'business',
      width: 150,
      editable: true,
    },
    {
      title: 'Department',
      dataIndex: 'department',
      width: 120,
      editable: true,
    },
    {
      title: 'Reference Number',
      dataIndex: 'reference_number',
      width: 130,
      editable: true,
    },
    {
      title: 'Next Kin Name',
      dataIndex: 'next_kin_name',
      width: 130,
      editable: true,
    },
    {
      title: 'Next Kin Phone',
      dataIndex: 'next_kin_phone',
      width: 120,
      editable: true,
    },
    {
      title: 'Next Kin Relation',
      dataIndex: 'next_kin_relationship',
      width: 140,
      editable: true,
      inputType: 'select',
    },
    {
      title: 'Actions',
      dataIndex: 'operation',
      fixed: 'right',
      width: 120,
      render: (_, record) => {
        const editable = isEditing(record);
        return editable ? (
          <span>
            <Typography.Link
              onClick={() => save(record.key)}
              style={{ marginRight: 8 }}
            >
              Save
            </Typography.Link>
            <Popconfirm title="Sure to cancel?" onConfirm={cancel}>
              <a>Cancel</a>
            </Popconfirm>
          </span>
        ) : (
          <span>
            <Typography.Link
              disabled={editingKey !== ''}
              onClick={() => edit(record)}
              style={{ marginRight: 8 }}
            >
              Edit
            </Typography.Link>
            <Popconfirm
              title="Sure to delete?"
              onConfirm={() => handleDelete(record.key)}
            >
              <Typography.Link disabled={editingKey !== ''} type="danger">
                Delete
              </Typography.Link>
            </Popconfirm>
          </span>
        );
      },
    },
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        inputType: col.inputType || 'text',
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });

  const validateRow = (row) => {
    const errors = [];
    
    // Required fields validation
    if (!row.first_name || !row.first_name.trim()) errors.push('First Name is required');
    if (!row.last_name || !row.last_name.trim()) errors.push('Last Name is required');
    if (!row.email || !row.email.trim()) errors.push('Email is required');
    if (!row.phone || !row.phone.trim()) errors.push('Phone is required');
    if (!row.nrc || !row.nrc.trim()) errors.push('NRC is required');
    if (!row.address || !row.address.trim()) errors.push('Address is required');
    if (!row.province || !row.province.trim()) errors.push('Province is required');
    if (!row.employment_type || !row.employment_type.trim()) errors.push('Employment Type is required');
    if (!row.next_kin_name || !row.next_kin_name.trim()) errors.push('Next Kin Name is required');
    if (!row.next_kin_phone || !row.next_kin_phone.trim()) errors.push('Next Kin Phone is required');
    if (!row.next_kin_relationship || !row.next_kin_relationship.trim()) errors.push('Next Kin Relation is required');

    // Email validation
    if (row.email && !/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/.test(row.email)) {
      errors.push('Invalid email format');
    }

    // NRC/ID/Passport validation - removed format restriction
    // Allow any format for NRC/ID/Passport numbers

    // Phone validation
    const phonePattern = /^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s./0-9]*$/;
    if (row.phone && !phonePattern.test(row.phone)) {
      errors.push('Invalid phone number format');
    }
    if (row.next_kin_phone && !phonePattern.test(row.next_kin_phone)) {
      errors.push('Invalid next of kin phone format');
    }

    return errors;
  };

  const handleBulkUpload = async () => {
    if (data.length === 0) {
      message.warning('No data to upload. Please add borrower information.');
      return;
    }

    if (editingKey !== '') {
      message.warning('Please save or cancel the current edit before uploading.');
      return;
    }

    setUploading(true);
    const successes = [];
    const errors = [];

    for (let i = 0; i < data.length; i++) {
      const row = data[i];
      
      // Validate row
      const validationErrors = validateRow(row);
      if (validationErrors.length > 0) {
        errors.push({
          row: i + 1,
          data: row,
          error: validationErrors.join(', ')
        });
        continue;
      }

      try {
        // Check for duplicate NRC first
        try {
          const duplicateCheckResponse = await axios.get(
            `https://zedlenders.pickmesms.com/users?filter[nrc][_eq]=${row.nrc}&fields=id,nrc,first_name,last_name`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          
          if (duplicateCheckResponse.data.data && duplicateCheckResponse.data.data.length > 0) {
            const existingUser = duplicateCheckResponse.data.data[0];
            errors.push({
              row: i + 1,
              data: row,
              error: `Duplicate NRC/ID "${row.nrc}" - already exists for ${existingUser.first_name} ${existingUser.last_name}`,
            });
            continue; // Skip this row
          }
        } catch (duplicateCheckError) {
          console.error("Error checking for duplicate NRC in spreadsheet upload:", duplicateCheckError);
          // Continue with creation if the duplicate check fails
        }

        const payload = {
          first_name: row.first_name,
          last_name: row.last_name,
          email: row.email,
          phone: row.phone.toString(),
          nrc: row.nrc,
          address: row.address,
          province: row.province,
          employment_type: row.employment_type,
          industry: 'Business', // Default industry
          business: row.business,
          department: row.department,
          employee_number: row.reference_number || generateRefNumber(),
          next_kin_name: row.next_kin_name,
          next_kin_phone: row.next_kin_phone,
          next_kin_relationship: row.next_kin_relationship,
          company: company.id,
          role: 'ab3c07d3-a225-4b68-8454-96e00116e307',
          status: 'active',
          password: '123456',
          email_notifications: true,
          provider: 'default',
          credit_score: 650,
          documents: []
        };

        await axios.post(
          'https://zedlenders.pickmesms.com/users',
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        successes.push(i + 1);
      } catch (err) {
        errors.push({
          row: i + 1,
          data: row,
          error: err.response?.data?.errors?.[0]?.message || 'Unknown error'
        });
      }
    }

    setUploading(false);
    
    if (successes.length > 0) {
      message.success(`Successfully uploaded ${successes.length} borrower(s)`);
    }
    
    if (errors.length > 0) {
      message.error(`${errors.length} rows failed to upload. Check console for details.`);
      console.error('Upload errors:', errors);
    }

    if (successes.length > 0) {
      navigate('/borrowers');
    }
  };

  const handleExportCSV = () => {
    if (data.length === 0) {
      message.warning('No data to export');
      return;
    }

    const csvData = data.map(row => ([
      row.first_name,
      row.last_name,
      row.email,
      row.phone,
      row.nrc,
      row.address,
      row.province,
      row.employment_type,
      row.business,
      row.department,
      row.reference_number,
      row.next_kin_name,
      row.next_kin_phone,
      row.next_kin_relationship
    ]));

    const csv = Papa.unparse({
      fields: [
        'first_name', 'last_name', 'email', 'phone', 'nrc', 'address', 
        'province', 'employment_type', 'business', 'department', 'reference_number', 
        'next_kin_name', 'next_kin_phone', 'next_kin_relationship'
      ],
      data: csvData
    });

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'borrowers_bulk_upload.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImportCSV = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const csvData = e.target.result;
      const results = Papa.parse(csvData, {
        header: true,
        skipEmptyLines: true,
      });

      const importedData = results.data.map((row, index) => ({
        key: (Date.now() + index).toString(),
        first_name: row.first_name || '',
        last_name: row.last_name || '',
        email: row.email || '',
        phone: row.phone || '',
        nrc: row.nrc || '',
        address: row.address || '',
        province: row.province || '',
        employment_type: row.employment_type || '',
        business: row.business || '',
        department: row.department || '',
        reference_number: row.reference_number || generateRefNumber(),
        next_kin_name: row.next_kin_name || '',
        next_kin_phone: row.next_kin_phone || '',
        next_kin_relationship: row.next_kin_relationship || '',
      }));

      setData(importedData);
      message.success(`Imported ${importedData.length} rows from CSV`);
    };
    reader.readAsText(file);
    return false; // Prevent default upload
  };

  return (
    <Card>
      <div style={{ marginBottom: 16 }}>
        <Title level={3} style={{ textAlign: 'center', marginBottom: 16 }}>
          Bulk Borrower Upload - Live Editing Table
        </Title>
        
        <Space wrap style={{ marginBottom: 16 }}>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleAdd}
            disabled={editingKey !== ''}
          >
            Add Row
          </Button>
          <Button onClick={loadSampleData}>
            Load Sample Data
          </Button>
          <Button onClick={clearAllData} danger>
            Clear All
          </Button>
          <Button onClick={handleExportCSV}>
            Export CSV
          </Button>
          <Upload
            accept=".csv"
            beforeUpload={handleImportCSV}
            showUploadList={false}
          >
            <Button icon={<UploadOutlined />}>Import CSV</Button>
          </Upload>
        </Space>

        <div style={{ marginBottom: 8 }}>
          <Text type="secondary">
            • Click "Edit" to modify a row, then "Save" to confirm changes
          </Text>
        </div>
        <div style={{ marginBottom: 8 }}>
          <Text type="secondary">
            • Reference numbers will be auto-generated if left empty
          </Text>
        </div>
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">
            • All fields marked with * are required for upload
          </Text>
        </div>
      </div>

      <Form form={form} component={false}>
        <Table
          components={{
            body: {
              cell: EditableCell,
            },
          }}
          bordered
          dataSource={data}
          columns={mergedColumns}
          rowClassName="editable-row"
          pagination={{
            onChange: cancel,
            pageSize: 10,
          }}
          scroll={{ x: 1800 }}
          size="small"
        />
      </Form>

      <div style={{ marginTop: 16, textAlign: 'center' }}>
        <Button
          type="primary"
          size="large"
          loading={uploading}
          onClick={handleBulkUpload}
          disabled={uploading || editingKey !== ''}
        >
          {uploading ? 'Uploading...' : `Upload All Borrowers (${data.length})`}
        </Button>
      </div>
    </Card>
  );
};

export default BulkBorrowerSpreadsheet;